import { File, FileText, Download, Edit, MoreVertical, PenLine, Eye } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

export default function ListView({
  documents,
  selectedDocs,
  bulkMode,
  handleBulkSelect,
  handleDownload,
  handleEditMetadata,
  handlePreview,
  isPreviewable,
  canSignDocument,
  setSelectedDocForWorkflow,
  setSignatureModalOpen,
  isEditor,
  canEditDocument,
  canDownload,
  canSign,
  formatFileSize
}) {
  return (
    <div className="bg-slate-50/80 dark:bg-slate-800/80 rounded-lg border border-slate-300 dark:border-slate-600 divide-y divide-slate-200 dark:divide-slate-700 backdrop-blur-sm">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className={`flex items-center gap-4 p-3 hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer transition-colors ${selectedDocs.has(doc.id) ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
          onClick={() => {
            if (bulkMode) {
              handleBulkSelect(doc.id);
            } else if (isPreviewable && isPreviewable(doc.file_type)) {
              handlePreview(doc);
            }
          }}
        >
          {bulkMode && (
            <Checkbox
              checked={selectedDocs.has(doc.id)}
              onCheckedChange={() => handleBulkSelect(doc.id)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className={`w-8 h-8 rounded flex items-center justify-center ${
            doc.file_type?.toLowerCase() === 'pdf' ? 'bg-red-500/20 border border-red-500/30' : 'bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600'
          }`}>
            {doc.file_type?.toLowerCase() === 'pdf' ? (
              <File className="w-4 h-4 text-red-400" />
            ) : (
              <FileText className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{doc.name}</p>
          </div>
          <span className="text-xs text-slate-600 dark:text-slate-400 uppercase">{doc.file_type}</span>
          <span className="text-xs text-slate-600 dark:text-slate-400">{formatFileSize(doc.file_size)}</span>
          {canSignDocument(doc) && canSign && canSign() && (
            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px]">Sign Required</Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
              {isPreviewable && isPreviewable(doc.file_type) && (
                <DropdownMenuItem onClick={() => handlePreview(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Eye className="w-4 h-4 mr-2" /> Preview
                </DropdownMenuItem>
              )}
              {(!canDownload || canDownload()) && (
                <DropdownMenuItem onClick={() => handleDownload(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Download className="w-4 h-4 mr-2" /> Download
                </DropdownMenuItem>
              )}
              {canEditDocument && canEditDocument(doc) && (
                <DropdownMenuItem onClick={() => handleEditMetadata(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
              )}
              {canSignDocument(doc) && canSign && canSign() && (
                <DropdownMenuItem onClick={() => { setSelectedDocForWorkflow(doc); setSignatureModalOpen(true); }} className="text-amber-400 hover:bg-amber-500/20">
                  <PenLine className="w-4 h-4 mr-2" /> Sign
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}
    </div>
  );
}
